export const INDEXED_DB_WRITE_COALESCE_MS = 250;

type SetTimer = (
  callback: () => void,
  ms: number,
) => ReturnType<typeof setTimeout>;
type ClearTimer = (timer: ReturnType<typeof setTimeout>) => void;

interface WriteCoalescerOptions<T> {
  delayMs: number;
  write: (key: string, value: T) => Promise<void>;
  setTimer?: SetTimer;
  clearTimer?: ClearTimer;
}

export class WriteCoalescer<T> {
  private readonly delayMs: number;
  private readonly write: (key: string, value: T) => Promise<void>;
  private readonly setTimer: SetTimer;
  private readonly clearTimer: ClearTimer;
  private readonly pendingValues: Map<string, T> = new Map();
  private readonly timers: Map<string, ReturnType<typeof setTimeout>> =
    new Map();

  constructor(opts: WriteCoalescerOptions<T>) {
    this.delayMs = opts.delayMs;
    this.write = opts.write;
    const setTimer: SetTimer = opts.setTimer ?? setTimeout;
    const clearTimer: ClearTimer = opts.clearTimer ?? clearTimeout;
    this.setTimer = (callback, ms) => setTimer(callback, ms);
    this.clearTimer = (timer) => clearTimer(timer);
  }

  schedule(key: string, value: T): void {
    this.pendingValues.set(key, value);

    const existingTimer = this.timers.get(key);
    if (existingTimer) {
      this.clearTimer(existingTimer);
    }

    const timer = this.setTimer(() => {
      this.timers.delete(key);
      this.commit(key).catch(() => undefined);
    }, this.delayMs);
    this.timers.set(key, timer);
  }

  peek(key: string): T | undefined {
    return this.pendingValues.get(key);
  }

  cancel(key: string): void {
    const timer = this.timers.get(key);
    if (timer) {
      this.clearTimer(timer);
      this.timers.delete(key);
    }
    this.pendingValues.delete(key);
  }

  async flush(): Promise<void> {
    const keys = Array.from(this.pendingValues.keys());
    for (const key of keys) {
      const timer = this.timers.get(key);
      if (timer) {
        this.clearTimer(timer);
        this.timers.delete(key);
      }
    }

    await Promise.all(keys.map((key) => this.commit(key)));
  }

  private async commit(key: string): Promise<void> {
    if (!this.pendingValues.has(key)) {
      return;
    }
    const value = this.pendingValues.get(key) as T;
    this.pendingValues.delete(key);
    await this.write(key, value);
  }
}
